// @spec AST-STR-050
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  CreateGapDetectionRuleDialog,
  DeleteGapDetectionRuleDialog,
  EditGapDetectionRuleDialog,
  GapDetectionRuleList,
} from '@/features/settings/assets/gap-rules';
import type { GapDetectionRule } from '@/features/settings/assets/gap-rules';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/gap-detection-rules')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.gapDetectionRules'),
  }),
  component: GapDetectionRulesPage,
});

/**
 * Gap detection rules management page.
 *
 * Shows list of gap detection rules and allows admins to create, edit, and delete rules.
 */
function GapDetectionRulesPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<GapDetectionRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<GapDetectionRule | null>(null);

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  // Don't render content while checking permissions
  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Show skeleton while loading org info
  if (isRoleLoading || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl space-y-6">
          <header>
            <Skeleton className="h-9 w-56" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">
            {t('gapDetectionRulesPage.title')}
          </h1>
          <p className="mt-1 text-muted-foreground">{t('gapDetectionRulesPage.description')}</p>
        </header>

        <GapDetectionRuleList
          onCreateClick={() => {
            setShowCreateDialog(true);
          }}
          onEditClick={(rule) => {
            setEditingRule(rule);
          }}
          onDeleteClick={(rule) => {
            setDeletingRule(rule);
          }}
          isAdmin={isAdmin}
        />

        {/* Create Dialog */}
        <CreateGapDetectionRuleDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />

        {/* Edit Dialog */}
        <EditGapDetectionRuleDialog
          rule={editingRule}
          open={editingRule !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingRule(null);
            }
          }}
        />

        {/* Delete Dialog */}
        <DeleteGapDetectionRuleDialog
          rule={deletingRule}
          open={deletingRule !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeletingRule(null);
            }
          }}
        />
      </div>
    </div>
  );
}
